/**
 * AI-processing consent state, as every record serializer carries it (FLOW-290).
 *
 * 🚨 **FLOW-297: it is the BUSINESS's state** (`§A6.7.2`) — the same for every
 * person at the business, whichever record it rides on. A Kickstart, Debtor
 * Finance or pre-application payload carries its business's consent.
 *
 * Identical on the client and Ops payloads by design: the server's
 * `ai_consent_state` never returns who withdrew or why, so there is no
 * client-only variant of this shape to keep in step.
 *
 * `null` (rather than a state) means consent was never given for the business
 * — a different thing from withdrawn, and the screens must not conflate them:
 * never-given shows the consent checkbox, withdrawn shows the checkbox *and*
 * says when it was withdrawn.
 */
export interface AiConsentState {
  readonly state: 'ACTIVE' | 'WITHDRAWN';
  /** ISO-8601 UTC — render with DatePipe, never sliced (see datetime-timezones). */
  readonly consented_at: string;
  readonly consent_text_version: string;
  /** `FC-DET-065`: an agent-given consent is recorded as the agent's, for the business. */
  readonly given_as?: 'MEMBER' | 'AGENT' | '';
  /** When it took effect — for an emailed withdrawal, when the email arrived. */
  readonly revoked_at: string | null;
  /**
   * True when Ops keyed a withdrawal that arrived by email, rather than
   * someone withdrawing on a screen.
   */
  readonly recorded_by_ops: boolean;
  readonly withdrawal_route?: AiConsentWithdrawalRoute | '';
  /** When the withdrawal was recorded — shown beside `revoked_at` (`UI-DA-60-B-03`). */
  readonly withdrawal_recorded_at?: string | null;
  readonly director_check_pending?: boolean;
}

type AiConsentWithdrawalRoute = 'GIVER' | 'DIRECTOR' | 'MEMBER_MAY_GIVE' | 'AGENT' | 'OPS_EMAIL';

/** The route, as a reader tells them apart (`UI-DA-60-B-07`, `-C-04`). */
export const WITHDRAWAL_ROUTE_LABELS: Record<AiConsentWithdrawalRoute, string> = {
  GIVER: 'by the person who gave it',
  DIRECTOR: 'by a director',
  MEMBER_MAY_GIVE: 'by a member of the business',
  AGENT: 'by the introducing agent',
  OPS_EMAIL: 'by email, recorded by CapX',
};

export type AiConsentSenderStanding =
  'GIVER' | 'DIRECTOR' | 'CIPC_DISCLOSURE' | 'MEMBER_MAY_GIVE' | 'AGENT' | 'UNCONFIRMED';

/**
 * Whether a screen must put the consent question to this person (`FC-DET-055`).
 *
 * Not while the business's consent stands — a second person is never asked.
 * Always after a withdrawal — the question is put again, and a sign-up tick
 * does not ride over it. Where it was never given, only if this person has no
 * sign-up tick for the server to carry to the business.
 */
export function aiConsentBoxNeeded(
  business: AiConsentState | null | undefined,
  hasSignUpConsent: boolean,
): boolean {
  if (business?.state === 'ACTIVE') return false;
  if (business?.state === 'WITHDRAWN') return true;
  return !hasSignUpConsent;
}

/** One row of the business's consent record as Ops reads it. */
export interface AiConsentOpsRow extends AiConsentState {
  readonly id: string;
  readonly consented_by: string;
  readonly revoked_by: string;
  readonly withdrawal_sender: string;
  readonly withdrawal_sender_standing: AiConsentSenderStanding | '';
  readonly withdrawal_standing_basis: string;
  readonly withdrawal_email_text: string;
  readonly director_check_required: boolean;
  readonly director_checks: readonly {
    readonly cipc_disclosure: string;
    readonly cipc_disclosure_date: string;
    readonly result: 'SHOWN' | 'NOT_SHOWN' | 'NOT_ESTABLISHED';
    readonly basis: string;
    readonly recorded_by: string;
    readonly recorded_at: string;
  }[];
}

export interface AiConsentOpsRecord {
  readonly entity_short_id: string;
  readonly ai_consent: AiConsentOpsRow | null;
  readonly history: readonly AiConsentOpsRow[];
}

/** The business consent block on the client's own profile. */
export interface BusinessAiConsent {
  readonly entity_short_id: string;
  readonly entity_name: string;
  readonly ai_consent: AiConsentState | null;
}
